import { useRef, useState, useEffect } from "react";
import DatePicker from "../../components/Shared/DatePicker";
import * as XLSX from "xlsx-js-style";
import { Menu } from "primereact/menu";
import { useSelector } from "react-redux";
import i18next from "i18next";
import SharedButton from "../../components/Shared/SharedButton";
import { useTranslation } from "react-i18next";
import { getOneMonthAgo } from "../CashInflow/CashInflow";
import VatReturnReportTable from "./VatReturnReportTable";
import useFetchData from "../../hooks/useFetchData";
import { FaFileExport } from "react-icons/fa6";

export default function VatReturnReport() {
  const [endDate, setEndDate] = useState(
    new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .split("T")[0],
  );
  const [startDate, setStartDate] = useState(getOneMonthAgo(endDate));
  const {
    data: taxReturnReportList,
    loading: isLoadingReport,
    executeFetch: executeFetchReport,
  } = useFetchData();
  const {
    data: settelmentList,
    loading: isLoadingSettelment,
    executeFetch: executeFetchSettelment,
  } = useFetchData();
  const balanceSheetRef = useRef(null);
  const iframeRef = useRef(null);
  const menuRef = useRef(null);
  const { t } = useTranslation();
  const lang = i18next.language;
  const { user } = useSelector((state) => state.auth);

  const isLoading = isLoadingReport || isLoadingSettelment;

  const fetchDat = async (payload) => {
    executeFetchReport("POST", "reports/getTaxReturnReport", payload);
    executeFetchSettelment(
      "GET",
      `reports/vat_report?start_date=${payload?.start_date}&end_date=${payload?.end_date}`,
    );
  };
  const handleSubmit = () => {
    if (startDate && endDate) {
      const payload = {
        start_date: startDate,
        end_date: endDate,
        page: 1,
        per_page: 20,
      };
      fetchDat(payload);
    }
  };

  const handleReset = () => {
    const defaultEndDate = new Date().toISOString().split("T")[0];
    const defaultStartDate = getOneMonthAgo(defaultEndDate);
    setEndDate(defaultEndDate);
    setStartDate(defaultStartDate);
    const payload = {
      start_date: defaultStartDate,
      end_date: defaultEndDate,
      page: 1,
      per_page: 20,
    };
    fetchDat(payload);
  };

  // useEffect(() => {
  //   const payload = {
  //     start_date: startDate,
  //     end_date: endDate,
  //     page: 1,
  //     per_page: 20
  //   };

  //   fetchDat(payload);
  // }, []);

  const handlePrint = () => {
    const direction = lang === "ar" ? "rtl" : "ltr";
    const iframe = iframeRef.current;
    const doc = iframe.contentWindow.document;

    const businessNameEn = user?.Business?.name_en || "";
    const businessNameAr = user?.Business?.name_ar || "";
    const businessAddress = user?.Business?.address || "";
    const userEmail = user?.email || "";
    const userMobile = user?.mobile_number || "";
    const businessLogo = user?.Business?.logo || user?.Business?.logo_url;

    // Taxpayer info
    const taxpayerName =
      lang === "ar"
        ? taxReturnReportList?.taxpayer_name_ar
        : taxReturnReportList?.taxpayer_name_en;
    const vatReg = taxReturnReportList?.vat_registration ?? "";
    const regDate = taxReturnReportList?.registration_date ?? "";
    const address = taxReturnReportList?.address ?? "";

    const vatPayable =
      (settelmentList?.data?.vat_payable?.total_debit ?? 0) -
      (settelmentList?.data?.vat_payable?.total_credit ?? 0);
    const vatReceivable =
      (settelmentList?.data?.vat_receiveable?.total_debit ?? 0) -
      (settelmentList?.data?.vat_receiveable?.total_credit ?? 0);
    const settlement = (vatPayable + vatReceivable).toFixed(2);

    const rowStyle =
      "padding: 10px 14px; display: flex; justify-content: space-between; font-size: 14px; border-bottom: 1px solid #d1d5db;";
    const labelStyle = "font-weight: 600;";
    const thStyle =
      "border: 1px solid #d1d5db; padding: 10px 12px; text-align: center; font-weight: 600; font-size: 13px;";
    const tdStyle =
      "border: 1px solid #d1d5db; padding: 10px 12px; text-align: center; font-size: 13px;";

    const contentHTML = `
      <div style="width: 100%;">
        <!-- Taxpayer Info Card -->
        <div style="background: #fff; border: 1px solid #d1d5db; border-radius: 6px; overflow: hidden; margin-bottom: 16px;">
          <div style="${rowStyle}">
            <span style="${labelStyle}">${t("Taxpayer Name")}:</span>
            <span>${taxpayerName ?? ""}</span>
          </div>
          <div style="${rowStyle}">
            <span style="${labelStyle}">${t("VAT Registration Number")}:</span>
            <span>${vatReg}</span>
          </div>
          <div style="${rowStyle}">
            <span style="${labelStyle}">${t("Effective Registration Date")}:</span>
            <span>${regDate}</span>
          </div>
          <div style="padding: 10px 14px; display: flex; justify-content: space-between; font-size: 14px;">
            <span style="${labelStyle}">${t("Taxpayer Address")}:</span>
            <span>${address}</span>
          </div>
        </div>

        <!-- VAT Table -->
        <div style="background: #fff; border-radius: 6px; margin-top: 16px;">
          <table style="width: 100%; border-collapse: collapse; font-family: sans-serif;">
            <thead>
              <tr style="background-color: #d1d5db;">
                <th style="${thStyle}">${t("tax")}</th>
                <th style="${thStyle}">${t("Vat Payable")}</th>
                <th style="${thStyle}">${t("Vat Receivable")}</th>
                <th style="${thStyle}">${t("Vat Amount")}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="${tdStyle}">${t("Settlement")}</td>
                <td style="${tdStyle}">${vatPayable.toFixed(2)}</td>
                <td style="${tdStyle}">${vatReceivable.toFixed(2)}</td>
                <td style="${tdStyle}">${settlement}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    `;

    doc.open();
    doc.write(
      `<html><head><title>VAT Return Report</title><style>@media print {@page {size: A4 portrait; margin: 15mm;} body {margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; font-family: 'Inter', sans-serif; direction: ${direction};} header, footer {display: none;}}</style></head><body style="font-family:sans-serif; direction:${direction}">`,
    );

    // 3-Column Business Header
    doc.write(`
    <div style="display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 20px; border-bottom: 2px solid #ccc; padding-bottom: 10px;">
      
      <!-- Column 1: Contact Info -->
      <div style="flex: 1; text-align: start; font-size: 13px; color: #333; line-height: 1.6;">
        ${businessAddress ? `<div><span style="font-weight: bold;">${t("Address")}:</span> ${businessAddress}</div>` : ""}
        ${userMobile ? `<div><span style="font-weight: bold;">${t("Phone")}:</span> ${userMobile}</div>` : ""}
        ${userEmail ? `<div><span style="font-weight: bold;">${t("Email")}:</span> ${userEmail}</div>` : ""}
      </div>

      <!-- Column 2: Logo and Main Info (Center) -->
      <div style="flex: 1; text-align: center;">
        ${businessLogo ? `<img src="${businessLogo}" style="height: 75px; margin-bottom: 8px; object-fit: contain;" />` : ""}
        ${businessNameEn ? `<div style="font-weight: bold; font-size: 18px; color: #1f2937;">${businessNameEn}</div>` : ""}
        ${businessNameAr && businessNameAr !== businessNameEn ? `<div style="font-weight: bold; font-size: 18px; color: #1f2937;">${businessNameAr}</div>` : ""}
        <div style="font-weight: bold; font-size: 16px; color: #1f2937; margin-top: 8px;">${t("Vat Return Report")}</div>
        <div style="font-size: 12px; color: #555; margin-top: 5px;">${t("From")}: ${startDate} - ${t("To")}: ${endDate}</div>
      </div>

      <!-- Column 3: Contact Info (Mirrored) -->
      <div style="flex: 1; text-align: end; font-size: 13px; color: #333; line-height: 1.6;">
        ${businessAddress ? `<div><span style="font-weight: bold;">${t("Address")}:</span> ${businessAddress}</div>` : ""}
        ${userMobile ? `<div><span style="font-weight: bold;">${t("Phone")}:</span> ${userMobile}</div>` : ""}
        ${userEmail ? `<div><span style="font-weight: bold;">${t("Email")}:</span> ${userEmail}</div>` : ""}
      </div>
      
    </div>
    `);

    doc.write(contentHTML);
    doc.write("</body></html>");
    doc.close();

    iframe.onload = () => {
      iframe.contentWindow.print();
    };
  };

  const extractData = (element) => {
    const data = [];

    // Extract taxpayer information
    const taxpayerInfo = element.querySelector(
      ".bg-white.rounded.px-4.py-2.mb-4.border",
    );
    if (taxpayerInfo) {
      data.push(["Taxpayer Information"]);
      taxpayerInfo.querySelectorAll(".flex.justify-between").forEach((item) => {
        const label = item.querySelector(".font-semibold").textContent.trim();
        const value = item.querySelector("span:last-child").textContent.trim();
        data.push([label, value]);
      });
      data.push([]);
    }

    // Extract VAT return table data
    const table = element.querySelector("table");
    if (table) {
      data.push(["VAT Return Details"]);
      table.querySelectorAll("tr").forEach((row) => {
        const rowData = Array.from(row.querySelectorAll("th, td")).map((cell) =>
          cell.textContent.trim(),
        );
        data.push(rowData);
      });
      data.push([]);
    }

    // Extract summary information
    const summary = element.querySelector(
      ".bg-white.rounded.px-4.py-2.mt-4.border",
    );
    if (summary) {
      data.push(["Summary"]);
      summary.querySelectorAll(".flex.justify-between").forEach((item) => {
        const label = item.querySelector(".font-semibold").textContent.trim();
        const value = item.querySelector("span:last-child").textContent.trim();
        data.push([label, value]);
      });
    }
    return data;
  };

  const handleExcelExport = () => {
    const reportTitle = t("Vat Return Report") || "Vat Return Report";
    const dateRange =
      startDate || endDate
        ? `${t("From")}: ${startDate || ""}  -  ${t("To")}: ${endDate || ""}`
        : "";
    const businessNameEn = user?.Business?.name_en || "";
    const businessNameAr = user?.Business?.name_ar || "";
    const displayBusinessName =
      lang === "ar"
        ? businessNameAr || businessNameEn
        : businessNameEn || businessNameAr;

    const wb = XLSX.utils.book_new();

    const headerAoa = [
      [`${displayBusinessName}`],
      [`${reportTitle}`],
      [`${dateRange}`],
      [],
    ];

    const extractedData = extractData(balanceSheetRef.current);
    const data = [...headerAoa, ...extractedData];

    const ws = XLSX.utils.aoa_to_sheet(data);

    let maxCols = 4;
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: maxCols - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: maxCols - 1 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: maxCols - 1 } },
    ];

    for (let r = 0; r < 3; r++) {
      const cellRef = XLSX.utils.encode_cell({ c: 0, r });
      if (ws[cellRef]) {
        ws[cellRef].s = {
          font: { bold: true, sz: r === 0 ? 16 : 14 },
          alignment: { horizontal: "center", vertical: "center" },
        };
      }
    }

    data.forEach((row, rowIndex) => {
      if (rowIndex >= 3) {
        if (row.length === 1 && row[0]) {
          const cellRef = XLSX.utils.encode_cell({ c: 0, r: rowIndex });
          if (ws[cellRef]) {
            ws[cellRef].s = {
              font: { bold: true, sz: 14 },
              alignment: { horizontal: "left", vertical: "center" },
            };
          }
          ws["!merges"].push({
            s: { r: rowIndex, c: 0 },
            e: { r: rowIndex, c: maxCols - 1 },
          });
        } else if (row.length > 1 && row[0] === t("tax")) {
          for (let c = 0; c < row.length; c++) {
            const cellRef = XLSX.utils.encode_cell({ c, r: rowIndex });
            if (ws[cellRef])
              ws[cellRef].s = {
                fill: { patternType: "solid", fgColor: { rgb: "D1D5DB" } },
                font: { bold: true },
                alignment: { horizontal: "center", vertical: "center" },
              };
          }
        }
      }
    });

    const colWidths = [{ wch: 30 }, { wch: 20 }, { wch: 20 }, { wch: 20 }];
    ws["!cols"] = colWidths;

    if (lang === "ar") {
      ws["!dir"] = "rtl";
    }

    XLSX.utils.book_append_sheet(wb, ws, "VAT Return Report");
    XLSX.writeFile(wb, "VAT Return Report.xlsx");
  };

  return (
    <div>
      <div className="flex justify-end">
        <div className="w-full md:w-fit flex gap-2 flex-col sm:flex-row">
          <DatePicker
            useFormik={false}
            value={startDate ? new Date(startDate) : null}
            onChange={(e) => {
              setStartDate(
                e.value
                  ? new Date(
                    e.value.getTime() - e.value.getTimezoneOffset() * 60000,
                  )
                    .toISOString()
                    .split("T")[0]
                  : "",
              );
            }}
          />
          <DatePicker
            useFormik={false}
            value={endDate ? new Date(endDate) : null}
            onChange={(e) => {
              setEndDate(
                e.value
                  ? new Date(
                    e.value.getTime() - e.value.getTimezoneOffset() * 60000,
                  )
                    .toISOString()
                    .split("T")[0]
                  : "",
              );
            }}
          />
          <div className="flex items-center justify-end gap-2">
            <SharedButton
              title={t("apply_filter")}
              icon="pi pi-filter"
              onClick={handleSubmit}
              loading={isLoading}
              disabled={!startDate || !endDate}
            />
            <SharedButton
              title={t("Reset")}
              icon="pi pi-sync"
              buttonType="reset"
              onClick={handleReset}
              disabled={isLoading}
            />
            <SharedButton
              title={t("Export Excel")}
              icon={"pi pi-file-excel"}
              iconSize={20}
              onClick={() => handleExcelExport()}
            />
            <SharedButton
              title={t("Export PDF")}
              icon={"pi pi-print"}
              iconSize={20}
              onClick={() => handlePrint()}
            />
          </div>
        </div>
      </div>

      <div className="this_div" ref={balanceSheetRef}>
        <div className="pb-2">
          {/* <div className='title !my-2'>
            <div className='flex justify-center'>
              <div className='mx-10 !mt-1'>
                <div className='font-semibold text-black/70 text-xl pb-2'>{`${t('VAT Return Report')}`}</div>
              </div>
            </div>
          </div> */}

          <div className="mt-2">
            <div
              className=""
              pt={{
                content: { className: "!py-0" },
              }}
            >
              <div className="">
                <VatReturnReportTable
                  data={{
                    address: taxReturnReportList?.address ?? "",
                    all_purchases: taxReturnReportList?.all_purchases ?? 0,
                    all_purchases_vat:
                      taxReturnReportList?.all_purchases_vat ?? 0,
                    all_sales: taxReturnReportList?.all_sales ?? 0,
                    all_sales_vat: taxReturnReportList?.all_sales_vat ?? 0,
                    registration_date:
                      taxReturnReportList?.registration_date ?? null,
                    tax_exempt_sale: taxReturnReportList?.tax_exempt_sale ?? 0,
                    tax_exempt_vat: taxReturnReportList?.tax_exempt_vat ?? 0,
                    taxpayer_name_ar:
                      taxReturnReportList?.taxpayer_name_ar ?? "",
                    taxpayer_name_en:
                      taxReturnReportList?.taxpayer_name_en ?? "",
                    total_sale: taxReturnReportList?.total_sale ?? 0,
                    total_sale_vat: taxReturnReportList?.total_sale_vat ?? 0,
                    vat_registration:
                      taxReturnReportList?.vat_registration ?? "",
                    vat_payable: settelmentList?.data?.vat_payable ?? 0,
                    vat_receiveable: settelmentList?.data?.vat_receiveable ?? 0,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <iframe ref={iframeRef} style={{ display: "none" }}></iframe>
    </div>
  );
}
